/* eslint-disable import/extensions */
/* eslint-disable global-require */
/* eslint-disable import/no-unresolved */
import React, { ReactElement, ReactChild } from "react";
import styled from "styled-components";

import { redesignTheme } from "../theme/theme";

type ButtonProps = {
  children: ReactChild;
  className?: string;
  disabled?: boolean;
  onClick: (args?: any) => void;
  size?: "xs" | "small" | "large";
  testId?: string;
  variant?:
    | "primary"
    | "secondary"
    | "superAdmin"
    | "icon"
    | "primaryInverse"
    | "secondaryInverse";
};

const StyledButton = styled.button<ButtonProps>`
  align-items: center;
  justify-content: center;
  background: ${({ disabled, variant }) => {
    if (disabled) return redesignTheme.color.grayAlpha100;
    switch (variant) {
      case "secondary":
      case "icon":
        return redesignTheme.color.grayAlpha100;
      case "superAdmin":
        return redesignTheme.color.yellow50;
      case "primaryInverse":
        return redesignTheme.color.white;
      case "secondaryInverse":
        return redesignTheme.color.gray900;
      case "primary":
      default:
        return redesignTheme.color.blue500;
    }
  }};
  border-radius: ${({ variant }) => {
    if (variant === "icon") return redesignTheme.radius.full;
    return redesignTheme.radius.md;
  }};
  border: none;
  }};
  color: ${({ disabled, variant }) => {
    if (disabled) return redesignTheme.color.gray300;
    switch (variant) {
      case "secondary":
      case "icon":
        return redesignTheme.color.gray800;
      case "superAdmin":
        return redesignTheme.color.gray900;
      case "primaryInverse":
        return redesignTheme.color.blue500;
      case "secondaryInverse":
      case "primary":
      default:
        return redesignTheme.color.white;
    }
  }};

  display: flex;
  font-family: ${redesignTheme.fontFamily.interface};
  font-size: ${redesignTheme.fontSize[100]};
  font-weight: ${redesignTheme.fontWeight.medium};
  letter-spacing: normal;
  line-height: 24px;
  text-transform: none;

  padding: ${({ size }) => {
    switch (size) {
      case "xs":
        return `${redesignTheme.spacing[1.5]} ${redesignTheme.spacing[3.5]}`;
      case "large":
        return `${redesignTheme.spacing[3.5]} ${redesignTheme.spacing[4.5]}`;
      case "small":
      default:
        return `${redesignTheme.spacing[2.5]} ${redesignTheme.spacing[4.5]}`;
    }
  }};

  box-shadow: none;

  width: ${({ variant }) => {
    if (variant === "icon") return "32px";
    return "auto";
  }};

  height: ${({ variant }) => {
    if (variant === "icon") return "32px";
    return "auto";
  }};

  &:hover {
    background: ${({ disabled, variant }) => {
      if (disabled) return redesignTheme.color.grayAlpha100;
      switch (variant) {
        case "secondary":
        case "icon":
          return redesignTheme.color.grayAlpha200;
        case "superAdmin":
          return redesignTheme.color.yellow100;
        case "primaryInverse":
          return redesignTheme.color.white;
        case "secondaryInverse":
          return redesignTheme.color.gray900;
        case "primary":
        default:
          return redesignTheme.color.blue600;
      }
    }};

    border: none;

    cursor: ${({ disabled }) => {
      if (disabled) return "not-allowed";
      return "pointer";
    }};
  }

  &:enabled:active {
    background: ${({ disabled, variant }) => {
      if (disabled) return redesignTheme.color.grayAlpha100;
      switch (variant) {
        case "secondary":
        case "icon":
          return redesignTheme.color.grayAlpha200;
        case "superAdmin":
          return redesignTheme.color.yellow100;
        case "primaryInverse":
          return redesignTheme.color.white;
        case "secondaryInverse":
          return redesignTheme.color.gray900;
        case "primary":
        default:
          return redesignTheme.color.blue600;
      }
    }};
    border: none;
  }
`;

const Button = ({
  children,
  className,
  disabled,
  onClick,
  size,
  testId,
  variant,
}: ButtonProps): ReactElement => {
  return (
    <StyledButton
      className={className}
      disabled={disabled}
      onClick={onClick}
      size={size}
      data-testid={testId}
      variant={variant}
    >
      {children}
    </StyledButton>
  );
};

Button.defaultProps = {
  className: "",
  disabled: false,
  size: "large",
  testId: "button",
  variant: "primary",
};

export default Button;
